import type { OrderStatus, ShipmentLeg, ShipmentStatus, ShippingMode } from '../../types/Procurement';
import type { StatusBadgeVariant } from '../ui/StatusBadge';

export const ORDER_STATUS_LABELS: Record<OrderStatus, string> = {
  draft: 'Draft',
  ordered: 'Ordered',
  'domestic-transit': 'Domestic transit',
  'at-warehouse': 'At warehouse',
  shipped: 'Shipped',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
};

/** The linear run an order moves along (cancelled sits outside it). */
export const ORDER_STEPS: Array<{ value: OrderStatus; label: string }> = (
  ['draft', 'ordered', 'domestic-transit', 'at-warehouse', 'shipped', 'delivered'] as OrderStatus[]
).map(value => ({ value, label: ORDER_STATUS_LABELS[value] }));

export const ORDER_STATUS_OPTIONS = (Object.keys(ORDER_STATUS_LABELS) as OrderStatus[]).map(value => ({ value, label: ORDER_STATUS_LABELS[value] }));

export const ORDER_STATUS_VARIANT: Record<OrderStatus, StatusBadgeVariant> = {
  draft: 'neutral',
  ordered: 'info',
  'domestic-transit': 'warning',
  'at-warehouse': 'purple',
  shipped: 'primary',
  delivered: 'success',
  cancelled: 'danger',
};

export const SHIPMENT_STATUS_LABELS: Record<ShipmentStatus, string> = { draft: 'Draft', active: 'Active', cancelled: 'Cancelled' };
export const SHIPMENT_STATUS_OPTIONS = (Object.keys(SHIPMENT_STATUS_LABELS) as ShipmentStatus[]).map(value => ({ value, label: SHIPMENT_STATUS_LABELS[value] }));

/** Legal stored-status moves for a shipment (anything else is a 400 illegal_transition). */
export const SHIPMENT_TRANSITIONS: Record<ShipmentStatus, ShipmentStatus[]> = {
  draft: ['active', 'cancelled'],
  active: ['cancelled'],
  cancelled: ['draft'],
};

export const SHIPMENT_LEG_LABELS: Record<ShipmentLeg, string> = {
  'domestic-origin': 'Domestic origin',
  international: 'International',
  'domestic-destination': 'Domestic destination',
};
export const SHIPMENT_LEG_OPTIONS = (Object.keys(SHIPMENT_LEG_LABELS) as ShipmentLeg[]).map(value => ({ value, label: SHIPMENT_LEG_LABELS[value] }));

export const SHIPMENT_MODE_LABELS: Record<ShippingMode, string> = { sea: 'Sea', air: 'Air', road: 'Road', rail: 'Rail', bike: 'Bike' };
export const SHIPMENT_MODE_OPTIONS = (Object.keys(SHIPMENT_MODE_LABELS) as ShippingMode[]).map(value => ({ value, label: SHIPMENT_MODE_LABELS[value] }));

export const PAYMENT_LABELS: Record<string, string> = { unpaid: 'Unpaid', partial: 'Part paid', paid: 'Paid' };
export const RECEIVING_LABELS: Record<string, string> = { none: 'Nothing received', partial: 'Part received', complete: 'Fully received' };

export const PROGRESS_VARIANT: Record<string, StatusBadgeVariant> = {
  Draft: 'neutral', Booked: 'info', 'In transit': 'warning', Arrived: 'purple', Notified: 'primary', Received: 'success', Cancelled: 'danger',
};

/** A quantity (number or numeric string) as a number for display and range checks. Quantities are not money. */
export const qty = (v: number | string | null | undefined): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/** Trims a quantity for display: "12", "2.5". */
export const fmtQty = (v: number | string | null | undefined): string => String(qty(v));
